import React, { useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { KEY_MEASURE_IDS } from './riskIndicatorConfig';
import { formatPoints, formatRiskValue } from './riskFormat';

const isYes = (value) => String(value || '').toLowerCase() === 'yes';

export function TierChip({ tier, points, size = 'small' }) {
  return (
    <Chip
      size={size}
      label={points === undefined ? tier.label : `${tier.label} · ${formatPoints(points)}`}
      sx={{ bgcolor: tier.color, color: '#fff', fontWeight: 700, height: size === 'small' ? 22 : 28 }}
    />
  );
}

const measureValue = (row, indicatorId) =>
  row.contributions.find((item) => item.id === indicatorId)?.value ?? null;

export function buildRiskCsv(rows, indicators) {
  const headers = [
    { label: 'Rank', key: 'rank' },
    { label: 'School', key: 'name' },
    { label: 'City', key: 'city' },
    { label: 'County', key: 'county' },
    { label: 'School district', key: 'district' },
    { label: 'Grade band', key: 'gradeBand' },
    { label: 'Steady state', key: 'steadyState' },
    { label: 'Phasing in/out', key: 'phaseInOut' },
    { label: 'Risk score', key: 'points' },
    { label: 'Risk tier', key: 'tier' },
    ...indicators.flatMap((indicator) => [
      { label: indicator.label, key: `${indicator.id}_value` },
      { label: `${indicator.label} (score 1-5)`, key: `${indicator.id}_score` },
    ]),
  ];
  const data = rows.map((row) => {
    const record = {
      rank: row.rank,
      name: row.school.name,
      city: row.school.city || '',
      county: row.school.county || '',
      district: row.school.schoolDistrictName || '',
      gradeBand: row.school.gradeBand || '',
      steadyState: row.school.steadyState || '',
      phaseInOut: row.school.phaseInOut || '',
      points: row.points === null ? '' : Number(row.points.toFixed(1)),
      tier: row.tier.label,
    };
    row.contributions.forEach((item) => {
      record[`${item.id}_value`] = item.missing ? '' : item.value;
      record[`${item.id}_score`] = item.missing ? '' : Number(item.score.toFixed(2));
    });
    return record;
  });
  return { headers, data };
}

const HEAD_SX = { fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap', bgcolor: 'grey.50' };

const RiskRankingTable = ({ rows, indicators, csvPrefix, onSelect, selectedId }) => {
  const [sort, setSort] = useState({ key: 'points', dir: 'desc' });
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const shownIndicators = showAll
    ? indicators
    : indicators.filter((indicator) => KEY_MEASURE_IDS.includes(indicator.id));

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? rows.filter((row) =>
          [row.school.name, row.school.city, row.school.county, row.school.schoolDistrictName]
            .filter(Boolean)
            .some((text) => text.toLowerCase().includes(query))
        )
      : rows;
    const valueOf = (row) => {
      if (sort.key === 'points') return row.points;
      if (sort.key === 'name') return row.school.name;
      return measureValue(row, sort.key);
    };
    return [...filtered].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, search, sort]);

  const toggleSort = (key) =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : key === 'points' ? 'desc' : 'asc' }
    );

  const sortable = (key, label) => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key ? sort.dir : 'asc'}
      onClick={() => toggleSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  const csv = useMemo(() => buildRiskCsv(rows, indicators), [rows, indicators]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search school, city, county, or district"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 300 }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
          label={<Typography variant="body2">Show all measures</Typography>}
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Sorted by greatest risk. Click a row for the school&apos;s risk profile.
        </Typography>
        <CSVLink
          data={csv.data}
          headers={csv.headers}
          filename={`${csvPrefix}-charter-enrollment-risk.csv`}
          style={{ textDecoration: 'none' }}
        >
          <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon />}>
            Export CSV
          </Button>
        </CSVLink>
      </Stack>

      <TableContainer sx={{ maxHeight: 640, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_SX}>Rank</TableCell>
              <TableCell sx={HEAD_SX}>{sortable('name', 'Charter school')}</TableCell>
              <TableCell sx={HEAD_SX}>Area</TableCell>
              <TableCell sx={HEAD_SX}>{sortable('points', 'Risk')}</TableCell>
              {shownIndicators.map((indicator) => (
                <TableCell key={indicator.id} sx={HEAD_SX} align="right">
                  {sortable(indicator.id, indicator.shortLabel)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow
                key={row.school.id}
                hover
                selected={row.school.id === selectedId}
                onClick={() => onSelect(row)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{row.rank}</TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {row.school.name}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                    {row.school.gradeBand && (
                      <Chip size="small" variant="outlined" label={row.school.gradeBand} sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {isYes(row.school.steadyState) && (
                      <Chip size="small" variant="outlined" label="Steady State" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {isYes(row.school.phaseInOut) && (
                      <Chip size="small" variant="outlined" label="Phasing In/Out" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {row.school.yearsOfData < 3 && (
                      <Chip size="small" color="warning" variant="outlined" label="Limited history" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  <Typography variant="body2">{row.school.city}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[row.school.county, row.school.schoolDistrictName].filter(Boolean).join(' · ')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TierChip tier={row.tier} points={row.points} />
                </TableCell>
                {shownIndicators.map((indicator) => (
                  <TableCell key={indicator.id} align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {formatRiskValue(measureValue(row, indicator.id), indicator.format)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!visibleRows.length && (
              <TableRow>
                <TableCell colSpan={4 + shownIndicators.length}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No charter schools match these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RiskRankingTable;
